import React, { useState, useEffect } from 'react';
import { Line, Bar, Pie } from 'react-chartjs-2';
import { 
  FaUsers, 
  FaUserPlus, 
  FaChartLine, 
  FaCheckCircle, 
  FaWeight, 
  FaHeartbeat,
  FaFire,
  FaCalendarWeek
} from 'react-icons/fa';
import axios from 'axios';
import 'chart.js/auto';
import './Dashboards.css';

const Dashboards = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const apiUrl = process.env.REACT_APP_API_URL_USER || 'http://localhost:3031';
        const response = await axios.get(`${apiUrl}/api/users/admin/stats`);
        setStats(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Erro ao buscar estatísticas:', error);
        setError('Não foi possível carregar as estatísticas');
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="dashboards-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Carregando estatísticas...</p>
        </div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="dashboards-container">
        <div className="error-message">
          <p>{error || 'Erro ao carregar dados'}</p>
        </div>
      </div>
    );
  }

  // Dados para gráfico de crescimento
  const crescimentoData = {
    labels: stats.crescimento.map(item => {
      const date = new Date(item.data);
      return `${date.getDate()}/${date.getMonth() + 1}`;
    }),
    datasets: [
      {
        label: 'Total de Usuários',
        data: stats.crescimento.map(item => item.total),
        borderColor: '#df710a',
        backgroundColor: 'rgba(223, 113, 10, 0.1)',
        tension: 0.4,
        fill: true,
      },
    ],
  };

  // Dados para gráfico de retenção
  const retencaoData = {
    labels: stats.retencao.map(item => item.semana),
    datasets: [
      {
        label: 'Usuários Ativos',
        data: stats.retencao.map(item => item.ativos),
        backgroundColor: '#e97f4b',
        borderColor: '#df710a',
        borderWidth: 2,
      },
    ],
  };

  // Preparar dados para mapa de calor
  const prepararMapaCalor = () => {
    const dados = [];
    const maxValue = Math.max(...stats.mapaCalor.semanas.flatMap(s => s.dias));
    
    stats.mapaCalor.semanas.forEach((semana, semanaIdx) => {
      semana.dias.forEach((valor, diaIdx) => {
        dados.push({
          semana: semana.semana,
          dia: stats.mapaCalor.diasSemana[diaIdx],
          valor: valor,
          intensidade: maxValue > 0 ? (valor / maxValue) * 100 : 0
        });
      });
    });
    
    return { dados, maxValue };
  };

  const mapaCalor = prepararMapaCalor();

  return (
    <div className="dashboards-container">
      <div className="dashboards-header">
        <h1>
          <FaChartLine className="header-icon" />
          Dashboard Administrativo
        </h1>
      </div>

      {/* Cards de Resumo */}
      <div className="stats-cards">
        <div className="stat-card card-primary">
          <div className="stat-icon">
            <FaUsers />
          </div>
          <div className="stat-content">
            <h3>Total de Usuários</h3>
            <p className="stat-value">{stats.resumo.totalUsuarios}</p>
          </div>
        </div>

        <div className="stat-card card-success">
          <div className="stat-icon">
            <FaUserPlus />
          </div>
          <div className="stat-content">
            <h3>Novos Hoje</h3>
            <p className="stat-value">{stats.resumo.novosHoje}</p>
          </div>
        </div>

        <div className="stat-card card-info">
          <div className="stat-icon">
            <FaCalendarWeek />
          </div>
          <div className="stat-content">
            <h3>Ativos Esta Semana</h3>
            <p className="stat-value">{stats.resumo.ativosSemana}</p>
          </div>
        </div>

        <div className="stat-card card-warning">
          <div className="stat-icon">
            <FaCheckCircle />
          </div>
          <div className="stat-content">
            <h3>Taxa de Conclusão</h3>
            <p className="stat-value">{stats.resumo.taxaConclusao}%</p>
            <p className="stat-subtitle">{stats.resumo.usuariosCompletos} completaram 12 semanas</p>
          </div>
        </div>
      </div>

      {/* Gráficos Principais */}
      <div className="charts-grid">
        <div className="chart-card">
          <h2 className="chart-title">
            <FaChartLine className="chart-icon" />
            Crescimento de Usuários (30 dias)
          </h2>
          <Line 
            data={crescimentoData} 
            options={{ 
              responsive: true,
              maintainAspectRatio: false,
              plugins: { 
                legend: { 
                  display: true,
                  position: 'top'
                },
                tooltip: {
                  mode: 'index',
                  intersect: false
                }
              },
              scales: {
                y: {
                  beginAtZero: true,
                  ticks: {
                    stepSize: 1
                  }
                }
              }
            }} 
          />
        </div>

        <div className="chart-card">
          <h2 className="chart-title">
            <FaCalendarWeek className="chart-icon" />
            Retenção por Semana (12 semanas)
          </h2>
          <Bar 
            data={retencaoData} 
            options={{ 
              responsive: true,
              maintainAspectRatio: false,
              plugins: { 
                legend: { 
                  display: true,
                  position: 'top'
                }
              },
              scales: {
                y: {
                  beginAtZero: true,
                  ticks: {
                    stepSize: 1
                  }
                }
              }
            }} 
          />
        </div>
      </div>

      {/* Mapa de Calor de Engajamento */}
      <div className="chart-card heatmap-card">
        <h2 className="chart-title">
          <FaFire className="chart-icon" />
          Mapa de Calor de Engajamento
          <span className="chart-subtitle">Dias da Semana vs Semanas do Programa</span>
        </h2>
        <div className="heatmap-container">
          <div className="heatmap-wrapper">
            <div className="heatmap-header">
              <div className="heatmap-corner"></div>
              {stats.mapaCalor.diasSemana.map((dia, idx) => (
                <div key={idx} className="heatmap-day-header">{dia.substring(0, 3)}</div>
              ))}
            </div>
            {stats.mapaCalor.semanas.map((semana, semanaIdx) => (
              <div key={semanaIdx} className="heatmap-row">
                <div className="heatmap-week-label">Sem {semana.semana}</div>
                {semana.dias.map((valor, diaIdx) => {
                  const intensidade = mapaCalor.maxValue > 0 
                    ? (valor / mapaCalor.maxValue) * 100 
                    : 0;
                  return (
                    <div
                      key={diaIdx}
                      className="heatmap-cell"
                      style={{
                        backgroundColor: `rgba(223, 113, 10, ${Math.max(0.1, intensidade / 100)})`,
                        borderColor: intensidade > 50 ? '#df710a' : 'transparent',
                        borderWidth: intensidade > 50 ? '2px' : '1px'
                      }}
                      title={`Semana ${semana.semana}, ${stats.mapaCalor.diasSemana[diaIdx]}: ${valor} usuários`}
                    >
                      {valor > 0 && <span className="heatmap-value">{valor}</span>}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
          <div className="heatmap-legend">
            <span>Menos</span>
            <div className="heatmap-legend-gradient"></div>
            <span>Mais</span>
          </div>
        </div>
      </div>

      {/* Indicadores de Saúde */}
      <div className="health-indicators">
        <div className="chart-card health-card">
          <h2 className="chart-title">
            <FaWeight className="chart-icon" />
            Indicadores de Saúde
          </h2>
          <div className="health-stats">
            <div className="health-stat-item">
              <div className="health-stat-icon">
                <FaWeight />
              </div>
              <div className="health-stat-content">
                <h3>Peso Médio</h3>
                <p className="health-stat-value">
                  {stats.saude.pesoMedio > 0 ? `${stats.saude.pesoMedio} kg` : 'N/A'}
                </p>
                <p className="health-stat-subtitle">
                  {stats.saude.totalComDados} usuários com dados
                </p>
              </div>
            </div>

            <div className="health-stat-item">
              <div className="health-stat-icon">
                <FaHeartbeat />
              </div>
              <div className="health-stat-content">
                <h3>IMC Médio</h3>
                <p className="health-stat-value">
                  {stats.saude.imcMedio > 0 ? stats.saude.imcMedio : 'N/A'}
                </p>
                <p className="health-stat-subtitle">
                  {stats.saude.totalComDados} usuários com dados
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboards;
